import type { StoreLanguage } from './schemas/tenant.schema';

// The words of the pages a storefront always has (cart, contact, search, collections, order tracking, not
// found) in each language a store may be served in, as a Shopify theme's locale file gives them. A store that
// chose French reads "Votre panier" and "Écrivez-nous" on pages its theme never drew.

/** Every word a derived page prints. */
export type DerivedPageWords = {
  searchPlaceholder: string; search: string; yourCart: string; collections: string; trackOrder: string; trackNote: string;
  searchTitle: string; notFound: string; notFoundNote: string; continueShopping: string; contactUs: string; ourStore: string;
  getDirections: string; getInTouch: string; name: string; email: string; phone: string; yourMessage: string; sendMessage: string;
  thanks: string; sendFailed: string;
};

/** The words in English, the fallback when a store has chosen no language. */
const EN: DerivedPageWords = {
  searchPlaceholder: 'Search products', search: 'Search', yourCart: 'Your cart', collections: 'Collections', trackOrder: 'Track your order',
  trackNote: 'Enter the order number from your confirmation and the phone you ordered with.', searchTitle: 'Search our store',
  notFound: 'Page not found', notFoundNote: 'The page you were looking for does not exist or has moved.', continueShopping: 'Continue shopping',
  contactUs: 'Contact us', ourStore: 'Our store', getDirections: 'Get directions', getInTouch: 'Get in touch', name: 'Name', email: 'Email',
  phone: 'Phone', yourMessage: 'Your message', sendMessage: 'Send message', thanks: 'Thank you — we will get back to you soon.',
  sendFailed: 'Your message could not be sent. Please try again.'
};

/** The words in French. */
const FR: DerivedPageWords = {
  searchPlaceholder: 'Rechercher des produits', search: 'Rechercher', yourCart: 'Votre panier', collections: 'Collections', trackOrder: 'Suivre votre commande',
  trackNote: 'Saisissez le numéro de commande de votre confirmation et le téléphone utilisé pour commander.', searchTitle: 'Rechercher dans la boutique',
  notFound: 'Page introuvable', notFoundNote: 'La page que vous cherchez n’existe pas ou a été déplacée.', continueShopping: 'Continuer vos achats',
  contactUs: 'Contactez-nous', ourStore: 'Notre boutique', getDirections: 'Itinéraire', getInTouch: 'Écrivez-nous', name: 'Nom', email: 'E-mail',
  phone: 'Téléphone', yourMessage: 'Votre message', sendMessage: 'Envoyer', thanks: 'Merci, nous vous répondrons très vite.',
  sendFailed: 'Votre message n’a pas pu être envoyé. Veuillez réessayer.'
};

/** The words in Arabic. */
const AR: DerivedPageWords = {
  searchPlaceholder: 'ابحث عن المنتجات', search: 'بحث', yourCart: 'سلة التسوق', collections: 'المجموعات', trackOrder: 'تتبع طلبك',
  trackNote: 'أدخل رقم الطلب من رسالة التأكيد والهاتف الذي طلبت به.', searchTitle: 'ابحث في المتجر',
  notFound: 'الصفحة غير موجودة', notFoundNote: 'الصفحة التي تبحث عنها غير موجودة أو تم نقلها.', continueShopping: 'متابعة التسوق',
  contactUs: 'اتصل بنا', ourStore: 'متجرنا', getDirections: 'الاتجاهات', getInTouch: 'تواصل معنا', name: 'الاسم', email: 'البريد الإلكتروني',
  phone: 'الهاتف', yourMessage: 'رسالتك', sendMessage: 'إرسال', thanks: 'شكرًا، سنعود إليك قريبًا.', sendFailed: 'تعذر إرسال رسالتك. يرجى المحاولة مرة أخرى.'
};

/** Every language's words. */
const WORDS: Readonly<Record<StoreLanguage, DerivedPageWords>> = { en: EN, fr: FR, ar: AR };

// Picks the derived pages' words for a store's language, English when it has chosen none.
export const derivedPageWords = (language: StoreLanguage | null | undefined): DerivedPageWords => WORDS[language ?? 'en'];
